import { Star, Sparkles, Lock, Award, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { categoryDef } from '@/lib/constants';
import { formatVND, formatPercent } from '@/lib/format';
import type { Monitor } from '@/lib/api';

const CATEGORY_BADGE_STYLE: Record<string, string> = {
  focal: 'bg-cat-focal/10 text-cat-focal border-cat-focal/20',
  oled: 'bg-cat-oled/10 text-cat-oled border-cat-oled/20',
  gaming: 'bg-cat-gaming/10 text-cat-gaming border-cat-gaming/20',
  graphic: 'bg-cat-graphic/10 text-cat-graphic border-cat-graphic/20',
  curved: 'bg-cat-curved/10 text-cat-curved border-cat-curved/20',
  popular: 'bg-cat-popular/10 text-cat-popular border-cat-popular/20',
  portable: 'bg-cat-portable/10 text-cat-portable border-cat-portable/20',
};

const STATUS_STYLE: Record<string, string> = {
  active: 'bg-teal-500/[0.06] text-teal-800 border-teal-500/25',
  discontinued: 'bg-amber-500/[0.06] text-amber-800 border-amber-500/20',
  incoming: 'bg-muted/60 text-muted-foreground border-border',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  discontinued: 'Ngừng KD',
  incoming: 'Sắp về',
};

interface FieldProps {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}

function Field({ label, children, mono }: FieldProps) {
  return (
    <div className="flex items-baseline gap-3 border-b border-border/40 py-2 last:border-0">
      <div className="w-32 shrink-0 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={cn('flex-1 text-sm', mono && 'tabular-nums')}>{children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-primary">{title}</h3>
      <div>{children}</div>
    </section>
  );
}

export interface SkuDetailModalProps {
  monitor: Monitor | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SkuDetailModal({ monitor, open, onOpenChange }: SkuDetailModalProps) {
  const [copied, setCopied] = useState(false);

  if (!monitor) return null;

  const cat = categoryDef(monitor.category);
  const flags = [
    monitor.is_focal ? { label: 'Focal', icon: Star, className: 'bg-cat-focal/10 text-cat-focal border-cat-focal/20' } : null,
    monitor.is_oled_focus ? { label: 'OLED Focus', icon: Sparkles, className: 'bg-cat-oled/10 text-cat-oled border-cat-oled/20' } : null,
    monitor.is_exclusive ? { label: 'Độc quyền', icon: Lock, className: 'bg-amber-500/[0.06] text-amber-800 border-amber-500/20' } : null,
    monitor.is_branding ? { label: 'Branding', icon: Award, className: 'bg-primary/10 text-primary border-primary/20' } : null,
  ].filter(Boolean) as Array<{ label: string; icon: typeof Star; className: string }>;

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(monitor.id);
      setCopied(true);
      toast.success('Đã copy SKU id');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Không copy được');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="font-semibold">{monitor.brand}</Badge>
            {cat && (
              <Badge
                variant="outline"
                className={cn('gap-1', CATEGORY_BADGE_STYLE[monitor.category] ?? '')}
              >
                <cat.icon className="h-3 w-3" />
                {cat.label}
              </Badge>
            )}
            <Badge variant="outline" className={cn(STATUS_STYLE[monitor.status] ?? '')}>
              {STATUS_LABEL[monitor.status] ?? monitor.status}
            </Badge>
            {flags.map((f) => (
              <Badge key={f.label} variant="outline" className={cn('gap-1', f.className)}>
                <f.icon className="h-3 w-3" />
                {f.label}
              </Badge>
            ))}
          </div>
          <DialogTitle className="text-left text-lg leading-snug">{monitor.name}</DialogTitle>
          <DialogDescription className="flex items-center gap-2 text-left">
            <code className="rounded bg-muted px-1.5 py-0.5 text-[11px] tabular-nums">{monitor.id}</code>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCopyId} title="Copy id">
              {copied ? <Check className="h-3 w-3 text-cat-graphic" /> : <Copy className="h-3 w-3" />}
            </Button>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <Section title="Thông số kỹ thuật">
            <Field label="Kích thước" mono>
              {monitor.size ? `${monitor.size}"` : '—'}
            </Field>
            <Field label="Độ phân giải">{monitor.resolution ?? '—'}</Field>
            <Field label="Tần số quét" mono>
              {monitor.refresh_rate ? `${monitor.refresh_rate} Hz` : '—'}
            </Field>
            <Field label="Loại panel">{monitor.panel_type ?? '—'}</Field>
            <Field label="Tags">{monitor.tags ?? '—'}</Field>
          </Section>

          <Section title="Định giá">
            <Field label="MSRP" mono>{formatVND(monitor.price_msrp)}</Field>
            <Field label="CT (giá vốn)" mono>{formatVND(monitor.price_ct)}</Field>
            <Field label="Web" mono>{formatVND(monitor.price_web)}</Field>
            <Field label="Discount %" mono>{formatPercent(monitor.discount_pct)}</Field>
            <Field label="Margin VND" mono>{formatVND(monitor.margin_vnd)}</Field>
            <Field label="Margin %" mono>{formatPercent(monitor.margin_pct)}</Field>
          </Section>

          <Section title="Phân phối & Vị trí">
            <Field label="NPP">{monitor.npp ?? '—'}</Field>
            <Field label="Node">{monitor.node_raw ?? '—'}</Field>
          </Section>

          <Section title="Audit">
            <Field label="Tạo">{new Date(monitor.created_at).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}</Field>
            <Field label="Cập nhật">{new Date(monitor.updated_at).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}</Field>
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
